import classNames, { Argument } from "classnames/dedupe";
import React, { FC, ReactNode, useCallback } from "react";
import { NavArrowNextSmallWhite } from "../../../icons";
import { Button } from "../../ActionsSelectors/Button/Button/Button";
import LinkButton from "../../ActionsSelectors/Button/LinkButton/LinkButton";
import ProductPrice, {
  onlyMainPrices,
} from "../../DataDisplay/Price/ProductPrice";
import { ILinkProps } from "../../ActionsSelectors/Link/Link";
import Tooltip from "../../DataDisplay/Tooltip/Tooltip";
import { TTooltipButtonRenderFn } from "../../DataDisplay/Tooltip/Tooltip.interface";
import { DropdownToggleStrategy } from "../Dropdown/Dropdown.interface";
import { IImageProps, Image } from "../MediaBuilder/Image/Image";
import SvgIcon from "../../Icons/SvgIcon";
import { TPrice } from "../../DataDisplay/Price/Price";

export type TShopLookMarker = {
  /** Unique identifier */
  id: string;
  /**
  Text of marker
  */
  title: ReactNode;
  /** Absolute position on the image/content */
  position: {
    top?: number | string;
    left?: number | string;
    right?: number | string;
    bottom?: number | string;
  };
  /** Link data for marker */
  link: ILinkProps;
  /** List of `Price` entities */
  prices: TPrice[];
};

export interface IShopLookMarkerProps {
  /** List of markers */
  markers: TShopLookMarker[];
  /** Main image */
  image: IImageProps;
  mainButtonText?: ReactNode;
  /** Additional classes */
  classes?: {
    /**
     * applied to outer 'div' element
     */
    root?: Argument;
    wrapper?: Argument;
  };
}

const ShopLookMarker: FC<IShopLookMarkerProps> = ({
  markers,
  image,
  classes,
  mainButtonText = "Shop the look",
}) => {
  const rootClasses = classNames("shop-look", classes?.root);
  const wrapperClasses = classNames("shop-look__wrapper", classes?.wrapper);
  const renderButton: TTooltipButtonRenderFn = useCallback(
    ({ opened }) => (
      <Button
        variant="circleMarker"
        classes={{
          root: classNames({
            "shop-look__marker--active": opened,
          }),
        }}
      />
    ),
    [],
  );
  return (
    <div className={rootClasses}>
      <div className={wrapperClasses}>
        <Image
          classes={{ root: "shop-look__image" }}
          {...image}
          data-testid="Shoplookmarker__image"
        />
        {markers.map((item) => {
          const mainPrices = onlyMainPrices(item.prices);
          return (
            <Tooltip
              key={item.id}
              classes={{ tooltip: "p-0" }}
              button={renderButton}
              buttonStyle={{
                position: "absolute",
                top: item.position.top,
                right: item.position.right,
                bottom: item.position.bottom,
                left: item.position.left,
              }}
              size="short"
              toggleStrategy={DropdownToggleStrategy.HOVER}
            >
              <LinkButton
                href={item.link}
                classes={{
                  root: "text-start text-color-white text-color-hover-white text-decoration-none p-4",
                  content: "border-0",
                }}
              >
                <div className="d-flex align-items-center min-w-0 text-color-pressed-white-hover mb-4">
                  <div className="tooltip-header mb-0 tooltip-header--wrap max-lines max-lines--2 overflow-hidden white-space-normal">
                    {item.title}
                  </div>
                  <div>
                    <NavArrowNextSmallWhite className="mx-2 flex-shrink-0" />
                  </div>
                </div>

                <span className="tooltip-header text-decoration-none">
                  <ProductPrice prices={mainPrices} theme="dark" />
                </span>
              </LinkButton>
            </Tooltip>
          );
        })}
        <div className="ms-6 ms-md-7 mb-6 mb-md-7 position-absolute start-0 bottom-0">
          <Button variant="hoverSlide" className="shop-look__btn">
            <SvgIcon icon="ShoppingBagIcon" />
            <span className="shop-look__text">{mainButtonText}</span>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ShopLookMarker;
